import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { 
  Mail, MessageSquare, Send, Users, Clock,
  Loader2, CheckCircle, XCircle, AlertCircle, Megaphone
} from 'lucide-react';

const notificationTypes = [
  { value: 'application_received', label: 'Application Received' },
  { value: 'application_status', label: 'Application Status Update' },
  { value: 'interview_scheduled', label: 'Interview Scheduled' },
  { value: 'interview_reminder', label: 'Interview Reminder' },
  { value: 'welcome', label: 'Welcome Email' },
  { value: 'class_reminder', label: 'Class Reminder' },
  { value: 'enrollment_confirmed', label: 'Enrollment Confirmed' },
  { value: 'badge_earned', label: 'Badge Earned' },
  { value: 'certificate_ready', label: 'Certificate Ready' },
];

const statusColors = {
  sent: 'bg-green-100 text-green-800',
  delivered: 'bg-blue-100 text-blue-800',
  pending: 'bg-yellow-100 text-yellow-800',
  failed: 'bg-red-100 text-red-800',
  bounced: 'bg-orange-100 text-orange-800',
};

export default function AdminCommunicationsPage({ embedded = false }) {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState({ email: null, sms: null });
  const [logs, setLogs] = useState([]);
  
  // Email form
  const [emailForm, setEmailForm] = useState({
    to_email: '',
    to_name: '',
    subject: '',
    html_content: '',
  });
  
  // SMS form
  const [smsForm, setSmsForm] = useState({
    to_phone: '',
    message: '',
  });
  
  // Broadcast form
  const [broadcastForm, setBroadcastForm] = useState({
    subject: '',
    html_content: '',
    target_roles: ['parent'],
    delivery_method: 'email',
  });

  // Notification triggers
  const [triggers, setTriggers] = useState([]);
  const [editingTrigger, setEditingTrigger] = useState(null);

  useEffect(() => {
    fetchStatus();
    fetchLogs();
    fetchTriggers();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await api.get('/communications/status');
      const data = response.data;
      setStatus({
        email: data.sendgrid || data.email || null,
        sms: data.twilio || data.sms || null,
      });
    } catch (error) {
      console.error('Failed to fetch status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await api.get('/communications/logs?limit=50');
      // Handle both paginated (Django) and non-paginated responses
      setLogs(response.data?.results || response.data || []);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      setLogs([]);
    }
  };

  const fetchTriggers = async () => {
    try {
      const response = await api.get('/communications/triggers');
      setTriggers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch triggers:', error);
    }
  };

  const updateTrigger = async (triggerId, updates) => {
    try {
      await api.patch(`/communications/triggers/${triggerId}`, updates);
      toast.success('Trigger updated');
      fetchTriggers();
    } catch (error) {
      toast.error('Failed to update trigger');
    }
  };

  const testTrigger = async (triggerId) => {
    try {
      const response = await api.post(`/communications/triggers/${triggerId}/test`);
      toast.success(response.data.message);
      fetchLogs();
    } catch (error) {
      toast.error('Failed to send test notification');
    }
  };


  const sendEmail = async () => {
    if (!emailForm.to_email || !emailForm.subject || !emailForm.html_content) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSending(true);
    try {
      const response = await api.post('/communications/send-email', emailForm);
      if (response.data.success) {
        toast.success('Email sent successfully!');
        setEmailForm({ to_email: '', to_name: '', subject: '', html_content: '' });
        fetchLogs();
      } else {
        toast.error('Failed to send email: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const sendSMS = async () => {
    if (!smsForm.to_phone || !smsForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSending(true);
    try {
      const response = await api.post('/communications/send-sms', {
        to: smsForm.to_phone,
        message: smsForm.message,
      });
      toast.success(`SMS sent! Status: ${response.data.status || 'queued'}`);
      setSmsForm({ to_phone: '', message: '' });
      fetchLogs();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Unknown error';
      toast.error('SMS failed: ' + detail);
    } finally {
      setSending(false);
    }
  };

  const sendBroadcast = async () => {
    if (!broadcastForm.subject || !broadcastForm.html_content) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSending(true);
    try {
      const response = await api.post('/communications/broadcast', broadcastForm);
      toast.success(`Broadcast sent! ${response.data.sent} delivered, ${response.data.failed} failed`);
      setBroadcastForm({ subject: '', html_content: '', target_roles: ['parent'], delivery_method: 'email' });
      fetchLogs();
    } catch (error) {
      toast.error('Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-communications-page">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold mb-2">Communications</h1>
        <p className="text-muted-foreground">Send emails, SMS messages, and broadcast notifications</p>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="card-dashboard">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="h-8 w-8 text-primary" />
                <div>
                  <p className="font-medium">Email (SendGrid)</p>
                  <p className="text-sm text-muted-foreground">{status.email?.from_email || 'Not configured'}</p>
                </div>
              </div>
              <Badge variant={status.email?.configured ? 'default' : 'secondary'}>
                {status.email?.configured ? <CheckCircle className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                {status.email?.configured ? 'Connected' : 'Not Set Up'}
              </Badge>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-dashboard">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageSquare className="h-8 w-8 text-secondary" />
                <div>
                  <p className="font-medium">SMS (Twilio)</p>
                  <p className="text-sm text-muted-foreground">{status.sms?.phone_number || status.sms?.from_phone || 'Not configured'}</p>
                </div>
              </div>
              <Badge variant={status.sms?.configured ? 'default' : 'secondary'}>
                {status.sms?.configured ? <CheckCircle className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                {status.sms?.configured ? 'Connected' : 'Not Set Up'}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Configuration Help */}
      {(!status.email?.configured || !status.sms?.configured) && (
        <Card className="card-dashboard border-yellow-300 bg-yellow-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">Configuration Required</p>
                <p className="text-sm text-yellow-700">
                  To enable communications, add the following to your <code className="bg-yellow-100 px-1 rounded">.env</code> file:
                </p>
                {!status.email?.configured && (
                  <p className="text-sm text-yellow-700 mt-2">
                    <strong>SendGrid:</strong> Get your API key from <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer" className="underline">sendgrid.com</a>
                  </p>
                )}
                {!status.sms?.configured && (
                  <p className="text-sm text-yellow-700 mt-1">
                    <strong>Twilio:</strong> Get credentials from <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="underline">twilio.com</a>
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="email" className="space-y-6">
        <TabsList>
          <TabsTrigger value="email">Send Email</TabsTrigger>
          <TabsTrigger value="sms">Send SMS</TabsTrigger>
          <TabsTrigger value="triggers">Triggers</TabsTrigger>
          <TabsTrigger value="broadcast">Broadcast</TabsTrigger>
          <TabsTrigger value="logs">Logs</TabsTrigger>
        </TabsList>

        {/* Email Tab */}
        <TabsContent value="email">
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Send Email
              </CardTitle>
              <CardDescription>Send a custom email to a recipient</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Recipient Email *</Label>
                  <Input
                    type="email"
                    value={emailForm.to_email}
                    onChange={(e) => setEmailForm(f => ({...f, to_email: e.target.value}))}
                    placeholder="recipient@example.com"
                    data-testid="email-to"
                  />
                </div>
                <div>
                  <Label>Recipient Name</Label>
                  <Input
                    value={emailForm.to_name}
                    onChange={(e) => setEmailForm(f => ({...f, to_name: e.target.value}))}
                    placeholder="John Doe"
                  />
                </div>
              </div>
              <div>
                <Label>Subject *</Label>
                <Input
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm(f => ({...f, subject: e.target.value}))}
                  placeholder="Email subject"
                  data-testid="email-subject"
                />
              </div>
              <div>
                <Label>Content (HTML) *</Label>
                <Textarea
                  value={emailForm.html_content}
                  onChange={(e) => setEmailForm(f => ({...f, html_content: e.target.value}))}
                  placeholder="<p>Your email content here...</p>"
                  rows={6}
                  data-testid="email-content"
                />
              </div>
              <Button onClick={sendEmail} disabled={sending || !status.email?.configured} data-testid="send-email-btn">
                {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Send Email
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SMS Tab */}
        <TabsContent value="sms">
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Send SMS
              </CardTitle>
              <CardDescription>Send a text message</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {status.sms?.phone_number === '+15005550006' && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3 text-sm">
                  <strong>Test Mode:</strong> Twilio test credentials can only send to the magic number <code className="bg-amber-100 px-1 rounded">+15005550006</code>. Switch to live credentials to send to real numbers.
                </div>
              )}
              <div>
                <Label>Phone Number *</Label>
                <Input
                  type="tel"
                  value={smsForm.to_phone}
                  onChange={(e) => setSmsForm(f => ({...f, to_phone: e.target.value}))}
                  placeholder="+1 (555) 123-4567"
                  data-testid="sms-phone"
                />
              </div>
              <div>
                <Label>Message * ({smsForm.message.length}/1600)</Label>
                <Textarea
                  value={smsForm.message}
                  onChange={(e) => setSmsForm(f => ({...f, message: e.target.value.slice(0, 1600)}))}
                  placeholder="Your SMS message..."
                  rows={3}
                  maxLength={1600}
                  data-testid="sms-message"
                />
              </div>
              <Button onClick={sendSMS} disabled={sending || !status.sms?.configured} data-testid="send-sms-btn">
                {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Send SMS
              </Button>
            </CardContent>
          </Card>
        </TabsContent>


        {/* Notification Triggers Tab */}
        <TabsContent value="triggers">
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Notification Triggers
              </CardTitle>
              <CardDescription>
                Configure which events automatically send emails or text messages. Toggle channels per event.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {triggers.map((trigger) => {
                  const icon = {
                    registration: '👤',
                    password_reset: '🔑',
                    enrollment_confirmed: '✅',
                    enrollment_cancelled: '❌',
                    payment_success: '💳',
                    camp_enrollment: '🏕️',
                  }[trigger.event] || '📧';
                  
                  return (
                  <div key={trigger.id} className={`border rounded-lg overflow-hidden ${trigger.is_active ? 'border-border' : 'border-border/50 opacity-60'}`} data-testid={`trigger-${trigger.event}`}>
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{icon}</span>
                        <div>
                          <h4 className="font-medium">{trigger.event_display}</h4>
                          <div className="flex gap-2 mt-1">
                            {trigger.email_enabled && (
                              <Badge className="bg-blue-100 text-blue-700 text-xs">
                                <Mail className="h-3 w-3 mr-1" /> Email
                              </Badge>
                            )}
                            {trigger.sms_enabled && (
                              <Badge className="bg-green-100 text-green-700 text-xs">
                                <MessageSquare className="h-3 w-3 mr-1" /> SMS
                              </Badge>
                            )}
                            {!trigger.email_enabled && !trigger.sms_enabled && (
                              <Badge variant="outline" className="text-xs text-muted-foreground">No channels active</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingTrigger(editingTrigger === trigger.id ? null : trigger.id)}
                          data-testid={`edit-trigger-${trigger.event}`}
                        >
                          {editingTrigger === trigger.id ? 'Close' : 'Edit'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => testTrigger(trigger.id)}
                          data-testid={`test-trigger-${trigger.event}`}
                        >
                          <Send className="h-3 w-3 mr-1" /> Test
                        </Button>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={trigger.is_active}
                            onCheckedChange={(checked) => updateTrigger(trigger.id, { is_active: checked })}
                            data-testid={`toggle-trigger-${trigger.event}`}
                          />
                          <span className="text-xs text-muted-foreground">Active</span>
                        </div>
                      </div>
                    </div>

                    {editingTrigger === trigger.id && (
                      <div className="border-t bg-muted/30 p-4 space-y-4">
                        {/* Channel toggles */}
                        <div className="flex gap-6">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={trigger.email_enabled}
                              onCheckedChange={(checked) => updateTrigger(trigger.id, { email_enabled: checked })}
                            />
                            <Mail className="h-4 w-4 text-blue-500" />
                            <span className="text-sm font-medium">Send Email</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={trigger.sms_enabled}
                              onCheckedChange={(checked) => updateTrigger(trigger.id, { sms_enabled: checked })}
                            />
                            <MessageSquare className="h-4 w-4 text-green-500" />
                            <span className="text-sm font-medium">Send SMS</span>
                          </label>
                        </div>

                        {/* Email template */}
                        {trigger.email_enabled && (
                          <div className="space-y-2 bg-blue-50/50 dark:bg-blue-950/10 rounded-lg p-3">
                            <Label className="text-sm font-medium flex items-center gap-1"><Mail className="h-3 w-3" /> Email Subject</Label>
                            <Input
                              defaultValue={trigger.email_subject}
                              onBlur={(e) => updateTrigger(trigger.id, { email_subject: e.target.value })}
                              placeholder="e.g. Welcome to Ready Set STEM, {first_name}!"
                              data-testid={`email-subject-${trigger.event}`}
                            />
                            <Label className="text-sm font-medium">Email Body Override</Label>
                            <Textarea
                              defaultValue={trigger.email_body}
                              onBlur={(e) => updateTrigger(trigger.id, { email_body: e.target.value })}
                              rows={3}
                              placeholder="Leave empty to use the default branded template. Use {first_name}, {course_name}, {session_info}, {amount} as placeholders..."
                              data-testid={`email-body-${trigger.event}`}
                            />
                            <p className="text-xs text-muted-foreground">A visually branded HTML email template is used by default. Override only if needed.</p>
                          </div>
                        )}

                        {/* SMS template */}
                        {trigger.sms_enabled && (
                          <div className="space-y-2 bg-green-50/50 dark:bg-green-950/10 rounded-lg p-3">
                            <Label className="text-sm font-medium flex items-center gap-1"><MessageSquare className="h-3 w-3" /> SMS Message</Label>
                            <Textarea
                              defaultValue={trigger.sms_body}
                              onBlur={(e) => updateTrigger(trigger.id, { sms_body: e.target.value })}
                              rows={2}
                              placeholder="Use {first_name}, {course_name}, {session_info} as placeholders..."
                              data-testid={`sms-body-${trigger.event}`}
                            />
                            <p className="text-xs text-muted-foreground">SMS limited to 1600 characters. Currently using test Twilio credentials.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}

                {triggers.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">No notification triggers configured. Run the seed script to create default triggers.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Broadcast Tab */}
        <TabsContent value="broadcast">
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Megaphone className="h-5 w-5" />
                Broadcast Message
              </CardTitle>
              <CardDescription>Send a message to multiple users</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Target Audience</Label>
                <div className="flex gap-4 mt-2">
                  {['parent', 'instructor', 'volunteer'].map(role => (
                    <div key={role} className="flex items-center space-x-2">
                      <Checkbox
                        id={`role-${role}`}
                        checked={broadcastForm.target_roles.includes(role)}
                        onCheckedChange={(checked) => {
                          setBroadcastForm(f => ({
                            ...f,
                            target_roles: checked 
                              ? [...f.target_roles, role]
                              : f.target_roles.filter(r => r !== role)
                          }));
                        }}
                      />
                      <label htmlFor={`role-${role}`} className="capitalize">{role}s</label>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <Label>Delivery Method</Label>
                <Select 
                  value={broadcastForm.delivery_method} 
                  onValueChange={(v) => setBroadcastForm(f => ({...f, delivery_method: v}))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email Only</SelectItem>
                    <SelectItem value="sms">SMS Only</SelectItem>
                    <SelectItem value="both">Email & SMS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subject *</Label>
                <Input
                  value={broadcastForm.subject}
                  onChange={(e) => setBroadcastForm(f => ({...f, subject: e.target.value}))}
                  placeholder="Broadcast subject"
                />
              </div>
              <div>
                <Label>Content (HTML) *</Label>
                <Textarea
                  value={broadcastForm.html_content}
                  onChange={(e) => setBroadcastForm(f => ({...f, html_content: e.target.value}))}
                  placeholder="<p>Your broadcast message...</p>"
                  rows={6}
                />
              </div>
              <Button onClick={sendBroadcast} disabled={sending} data-testid="send-broadcast-btn">
                {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Megaphone className="h-4 w-4 mr-2" />}
                Send Broadcast
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs">
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Notification Logs
              </CardTitle>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">No notifications sent yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Recipient</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Sent</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map(log => (
                      <TableRow key={log.id}>
                        <TableCell className="capitalize">
                          {log.notification_type?.replace(/_/g, ' ')}
                        </TableCell>
                        <TableCell>
                          {log.delivery_method === 'email' ? (
                            <Mail className="h-4 w-4" />
                          ) : (
                            <MessageSquare className="h-4 w-4" />
                          )}
                        </TableCell>
                        <TableCell>
                          {log.to_email || log.to_phone}
                        </TableCell>
                        <TableCell>
                          <Badge className={statusColors[log.status]}>
                            {log.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {new Date(log.sent_at).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
